import { useMemo } from 'react';
import type { UppyFile } from '@uppy/core';
import type { MultiFileEntry } from '../machines/multi-file-uploader-types';
import type { UppyMeta, UppyBody } from '../machines/universal-media-uploader-types';

/**
 * A file currently being uploaded with its progress.
 */
export interface UploadingFile {
  id: string;
  file: UppyFile<UppyMeta, UppyBody>;
  progress: number;
}

/**
 * A file that has completed uploading successfully.
 */
export interface CompletedFile {
  id: string;
  file: UppyFile<UppyMeta, UppyBody>;
  thumbnailUrl?: string;
  /** ISO 8601 timestamp from the API indicating when the file was uploaded */
  uploadedAt?: string;
}

/**
 * A file that failed to upload.
 */
export interface ErrorFile {
  id: string;
  error: Error;
  file?: UppyFile<UppyMeta, UppyBody>;
}

/**
 * Return type for the useUploadFileGroups hook.
 */
export interface UseUploadFileGroupsReturn {
  /** Files currently being uploaded */
  uploading: UploadingFile[];
  /** Files that have completed uploading */
  completed: CompletedFile[];
  /** Files that failed to upload */
  errors: ErrorFile[];
  /** Count of successfully uploaded files */
  uploadedCount: number;
}

/**
 * Categorizes files from the upload state machine into uploading, completed, and error groups.
 * Provides a cleaner API for rendering upload status in components.
 *
 * @param files - Map of file entries from useUniversalMediaUploader
 * @returns Object containing categorized file arrays and upload count
 *
 * @example
 * ```tsx
 * const uploadState = useUniversalMediaUploader('my-uploader', config);
 * const { uploading, completed, errors, uploadedCount } = useUploadFileGroups(uploadState.files);
 *
 * // Render uploading files
 * {uploading.map(({ id, file, progress }) => (
 *   <UniversalMediaUploaderStatus key={id} file={file} progress={progress} />
 * ))}
 *
 * // Render errors
 * {errors.map(({ id, error }) => (
 *   <Alert key={id} severity="error">{error.message}</Alert>
 * ))}
 * ```
 */
export const useUploadFileGroups = (
  files: Map<string, MultiFileEntry>,
): UseUploadFileGroupsReturn => useMemo(() => {
  const uploading: UploadingFile[] = [];
  const completed: CompletedFile[] = [];
  const errors: ErrorFile[] = [];

  files.forEach((entry, id) => {
    if (entry.error) {
      errors.push({ id, error: entry.error, file: entry.file });
    } else if (entry.attachmentData && entry.file) {
      completed.push({
        id,
        file: entry.file,
        thumbnailUrl: entry.attachmentData.url,
        uploadedAt: typeof entry.attachmentData.uploaded_at === 'string'
          ? entry.attachmentData.uploaded_at
          : undefined,
      });
    } else if (entry.file) {
      uploading.push({ id, file: entry.file, progress: entry.progress ?? 0 });
    }
  });

  completed.sort((a, b) => {
    if (!a.uploadedAt && !b.uploadedAt) return 0;
    if (!a.uploadedAt) return 1;
    if (!b.uploadedAt) return -1;
    return a.uploadedAt.localeCompare(b.uploadedAt);
  });

  return {
    uploading, completed, errors, uploadedCount: completed.length,
  };
}, [files]);
